import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL?.endsWith("/api") ? BACKEND_URL : `${BACKEND_URL}/api`;

const EMPTY_SHOOTER = {
  name: "",
  nra_number: "",
  cmp_number: "",
  rating: "",
  caliber_group: "Open",
  competitor_number: "",
  division: "Civilian",
  special_categories: [],
};

const SAMPLE_CSV = `name,nra_number,cmp_number,rating,caliber_group,competitor_number,division,special_categories
Jorgenson Travis,SEED00001,CMP001,HM,Open,101,Civilian,Veteran
Emmert-Traciak Lisa,SEED00002,CMP002,HM,Open,102,Service,Women
Dean Roy,SEED00003,CMP003,MA,Open,103,Civilian,Grand Senior
Toler Alan,SEED00004,CMP004,MA,22 Only,104,Civilian,Senior
`;

const RATING_OPTIONS = (
  <>
    <option value="">Select Rating</option>
    <option value="HM">HM - High Master</option>
    <option value="MA">MA - Master</option>
    <option value="EX">EX - Expert</option>
    <option value="SS">SS - Sharpshooter</option>
    <option value="MK">MK - Marksman</option>
    <option value="UNC">UNC - Unclassified</option>
  </>
);

const SPECIAL_OPTIONS = [
  "Grand Senior",
  "Senior",
  "Women",
  "Veteran",
  "Service and Police",
];
// Orthogonal add-ons that may combine with any age category (and each other).
const COMBINABLE_SPECIALS = ["Women", "Service and Police"];

function toFormShooter(s) {
  return {
    name: s.name || "",
    nra_number: s.nra_number || "",
    cmp_number: s.cmp_number || "",
    rating: s.rating || "",
    caliber_group: s.caliber_group || "Open",
    competitor_number:
      s.competitor_number === 0 || s.competitor_number
        ? String(s.competitor_number)
        : "",
    division: s.division || "Civilian",
    special_categories: Array.isArray(s.special_categories)
      ? [...s.special_categories]
      : [],
  };
}

function toPayload(form) {
  const cats = form.special_categories || [];
  return {
    name: form.name.trim(),
    nra_number: form.nra_number.trim() || null,
    cmp_number: form.cmp_number.trim() || null,
    rating: form.rating || null,
    caliber_group: form.caliber_group || "Open",
    competitor_number: form.competitor_number
      ? parseInt(form.competitor_number, 10)
      : null,
    division: form.division || "Civilian",
    special_categories: cats,
  };
}

function toggleSpecial(list, value) {
  const has = list.includes(value);
  if (has) return list.filter((x) => x !== value);
  // Women / Service and Police combine freely; Grand Senior / Senior / Veteran
  // are mutually exclusive age categories (pick at most one).
  if (COMBINABLE_SPECIALS.includes(value)) return [...list, value];
  const kept = list.filter((x) => COMBINABLE_SPECIALS.includes(x));
  return [...kept, value];
}

const ShootersList = () => {
  const [shooters, setShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newShooter, setNewShooter] = useState(EMPTY_SHOOTER);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_SHOOTER);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);
  const { isAdmin } = useAuth();

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(""), 4000);
  };

  const fetchShooters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shooters`);
      setShooters(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching shooters:", err);
      setError(
        err.response?.data?.detail || "Failed to load shooters. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShooters();
  }, [fetchShooters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewShooter((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddShooter = async (e) => {
    e.preventDefault();

    if (!newShooter.name.trim()) {
      setError("Shooter name is required");
      return;
    }

    setCreating(true);
    setError(null);
    setImportResult(null);

    try {
      const response = await axios.post(`${API}/shooters`, toPayload(newShooter));
      setShooters((prev) =>
        [...prev, response.data].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        )
      );
      setNewShooter(EMPTY_SHOOTER);
      showSuccess(`Shooter ${response.data.name} created`);
    } catch (err) {
      console.error("Error adding shooter:", err);
      setError(
        err.response?.data?.detail || "Failed to add shooter. Please try again."
      );
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (shooter) => {
    setEditingId(shooter.id);
    setEditForm(toFormShooter(shooter));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_SHOOTER);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    if (!editForm.name.trim()) {
      setError("Shooter name is required");
      return;
    }

    setSavingEdit(true);
    setError(null);
    try {
      const response = await axios.put(
        `${API}/shooters/${editingId}`,
        toPayload(editForm)
      );
      setShooters((prev) =>
        prev
          .map((s) => (s.id === editingId ? response.data : s))
          .sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, {
              sensitivity: "base",
            })
          )
      );
      showSuccess(`Updated ${response.data.name}`);
      cancelEdit();
    } catch (err) {
      console.error("Error updating shooter:", err);
      setError(
        err.response?.data?.detail || "Failed to update shooter. Please try again."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (shooter) => {
    if (deletingId) return;

    const ok = window.confirm(
      `Delete shooter "${shooter.name}"?\n\n` +
        "If they have scores, you will be asked to confirm a forced delete."
    );
    if (!ok) return;

    setDeletingId(shooter.id);
    setError(null);

    try {
      await axios.delete(`${API}/shooters/${shooter.id}`);
      setShooters((prev) => prev.filter((s) => s.id !== shooter.id));
      if (editingId === shooter.id) cancelEdit();
      showSuccess(`Deleted ${shooter.name}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      // Soft-fail path: shooter has scores — ask for force
      if (err.response?.status === 400 && String(detail).toLowerCase().includes("score")) {
        const forceOk = window.confirm(
          `${detail}\n\nForce delete "${shooter.name}" AND all of their scores across every match? This cannot be undone.`
        );
        if (!forceOk) {
          setDeletingId(null);
          return;
        }
        try {
          await axios.delete(`${API}/shooters/${shooter.id}?force=true`);
          setShooters((prev) => prev.filter((s) => s.id !== shooter.id));
          if (editingId === shooter.id) cancelEdit();
          showSuccess(`Force-deleted ${shooter.name} and their scores`);
        } catch (forceErr) {
          console.error("Force delete failed:", forceErr);
          setError(
            forceErr.response?.data?.detail ||
              "Failed to force-delete shooter. Please try again."
          );
        }
      } else {
        console.error("Error deleting shooter:", err);
        setError(detail || "Failed to delete shooter. Please try again.");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("Please select a .csv file");
      e.target.value = "";
      return;
    }

    setUploading(true);
    setError(null);
    setImportResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/shooters/bulk-csv`, formData);
      setImportResult(response.data);
      const { created, skipped, errors } = response.data;
      showSuccess(
        `CSV import finished: ${created} created, ${skipped} skipped, ${errors} errors`
      );
      if (created > 0) {
        await fetchShooters();
      }
    } catch (err) {
      console.error("Error uploading shooter CSV:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to import shooters from CSV. Please check the file format."
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const downloadSampleCsv = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "shooters_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="page text-center text-ink-500">Loading shooters...</div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title mb-2">Shooters</h1>
      <p className="page-subtitle mb-6">
        Global competitor directory. Profiles are managed by admins (shooters do not
        log in). For a specific match day roster, open that match → Roster.
      </p>

      {error && (
        <div className="alert-error mb-4" role="alert">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {success && (
        <div className="alert-success mb-4" role="status">
          {success}
        </div>
      )}

      {isAdmin() && (
        <>
          <div className="card card-pad mb-6">
            <h2 className="section-title mb-1">Add New Shooter</h2>
            <p className="text-sm text-ink-500 mb-4">
              Special categories (Grand Senior, Senior, Women, Veteran) drive High-* awards
              on Results Bulletins. Women and Service and Police may combine with the other categories.
            </p>
            <form onSubmit={handleAddShooter} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="field-label"
                  >
                    Shooter Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={newShooter.name}
                    onChange={handleInputChange}
                    placeholder="Enter shooter name"
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="nra_number"
                    className="field-label"
                  >
                    NRA Number (Optional)
                  </label>
                  <input
                    id="nra_number"
                    name="nra_number"
                    type="text"
                    value={newShooter.nra_number}
                    onChange={handleInputChange}
                    placeholder="Enter NRA number"
                    className="input"
                  />
                </div>
                <div>
                  <label
                    htmlFor="cmp_number"
                    className="field-label"
                  >
                    CMP Number (Optional)
                  </label>
                  <input
                    id="cmp_number"
                    name="cmp_number"
                    type="text"
                    value={newShooter.cmp_number}
                    onChange={handleInputChange}
                    placeholder="Enter CMP number"
                    className="input"
                  />
                </div>
                <div>
                  <label
                    htmlFor="rating"
                    className="field-label"
                  >
                    Class (Optional)
                  </label>
                  <select
                    id="rating"
                    name="rating"
                    value={newShooter.rating}
                    onChange={handleInputChange}
                    className="select"
                  >
                    {RATING_OPTIONS}
                  </select>
                </div>
                <div>
                  <label className="field-label">
                    Competitor #
                  </label>
                  <input
                    name="competitor_number"
                    type="number"
                    min="1"
                    value={newShooter.competitor_number}
                    onChange={handleInputChange}
                    placeholder="e.g. 132"
                    className="input"
                  />
                </div>
                <div>
                  <label className="field-label">
                    Division
                  </label>
                  <select
                    name="division"
                    value={newShooter.division}
                    onChange={handleInputChange}
                    className="select"
                  >
                    <option value="Civilian">Civilian</option>
                    <option value="Police">Police</option>
                    <option value="Service">Service</option>
                  </select>
                </div>
                <div>
                  <label className="field-label">
                    Caliber Group
                  </label>
                  <select
                    name="caliber_group"
                    value={newShooter.caliber_group}
                    onChange={handleInputChange}
                    className="select"
                  >
                    <option value="Open">Open</option>
                    <option value="22 Only">22 Only</option>
                    <option value="Metallic">Metallic</option>
                  </select>
                </div>
              </div>
              <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                <label className="block text-sm font-semibold text-purple-900 mb-2">
                  Special categories (for High Senior / Woman / GS / Veteran awards)
                </label>
                <div className="flex flex-wrap gap-4 text-sm">
                  {SPECIAL_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className="inline-flex items-center gap-2 bg-white border border-purple-100 px-3 py-2 rounded shadow-sm cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={(newShooter.special_categories || []).includes(opt)}
                        onChange={() =>
                          setNewShooter((prev) => ({
                            ...prev,
                            special_categories: toggleSpecial(
                              prev.special_categories || [],
                              opt
                            ),
                          }))
                        }
                      />
                      <span className="font-medium text-ink-800">{opt}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-purple-800 mt-2">
                  Pick at most one of Grand Senior / Senior / Veteran. Women and
                  Service and Police may also be selected alongside those.
                </p>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={creating}
                  className={`btn-primary ${
                    creating ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {creating ? "Adding..." : "Add Shooter"}
                </button>
              </div>
            </form>
          </div>

          <div className="card card-pad mb-6">
            <h2 className="section-title mb-2">Bulk Import from CSV</h2>
            <p className="text-sm text-ink-600 mb-4">
              Required: <code className="bg-ink-100 px-1 rounded">name</code>.
              Optional: nra_number, cmp_number, rating, caliber_group, competitor_number, division
              (Civilian/Police/Service), special_categories
              (e.g. <code className="bg-ink-100 px-1 rounded">Grand Senior|Women</code>
              or Senior, Veteran).
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="shooter-csv-upload"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`btn-primary ${
                  uploading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {uploading ? "Uploading..." : "Upload CSV"}
              </button>
              <button
                type="button"
                onClick={downloadSampleCsv}
                className="btn-secondary"
              >
                Download Template
              </button>
            </div>

            {importResult && (
              <div className="mt-4 rounded-xl ring-1 ring-ink-100 overflow-hidden">
                <div className="bg-ink-50 px-4 py-2 text-sm font-medium text-ink-700">
                  Import results — {importResult.created} created,{" "}
                  {importResult.skipped} skipped, {importResult.errors} errors
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="data-table">
                    <thead className="sticky top-0">
                      <tr>
                        <th className="text-left">Row</th>
                        <th className="text-left">Name</th>
                        <th className="text-left">Status</th>
                        <th className="text-left">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.results.map((row) => (
                        <tr key={`${row.row}-${row.name || ""}`}>
                          <td>{row.row}</td>
                          <td>{row.name || "—"}</td>
                          <td>
                            <span
                              className={
                                row.status === "created"
                                  ? "badge-green"
                                  : row.status === "skipped"
                                  ? "badge-amber"
                                  : "badge bg-rose-100 text-rose-800 ring-rose-200"
                              }
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="text-ink-600">{row.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Edit panel */}
      {isAdmin() && editingId && (
        <div className="mb-6 rounded-2xl bg-amber-50 p-5 sm:p-6 ring-1 ring-amber-200 shadow-card">
          <h2 className="section-title mb-4">Edit Shooter</h2>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="field-label">
                  Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, name: e.target.value }))
                  }
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="field-label">
                  NRA Number
                </label>
                <input
                  type="text"
                  value={editForm.nra_number}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, nra_number: e.target.value }))
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">
                  CMP Number
                </label>
                <input
                  type="text"
                  value={editForm.cmp_number}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, cmp_number: e.target.value }))
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">
                  Class
                </label>
                <select
                  value={editForm.rating}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, rating: e.target.value }))
                  }
                  className="select"
                >
                  {RATING_OPTIONS}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="field-label">
                  Competitor #
                </label>
                <input
                  type="number"
                  min="1"
                  value={editForm.competitor_number}
                  onChange={(e) =>
                    setEditForm((p) => ({
                      ...p,
                      competitor_number: e.target.value,
                    }))
                  }
                  className="input"
                />
              </div>
              <div>
                <label className="field-label">
                  Division
                </label>
                <select
                  value={editForm.division}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, division: e.target.value }))
                  }
                  className="select"
                >
                  <option value="Civilian">Civilian</option>
                  <option value="Police">Police</option>
                  <option value="Service">Service</option>
                </select>
              </div>
              <div>
                <label className="field-label">
                Caliber Group
                </label>
                <select
                value={editForm.caliber_group}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, caliber_group: e.target.value }))
                }
                className="select"
                >
                <option value="Open">Open</option>
                <option value="22 Only">22 Only</option>
                <option value="Metallic">Metallic</option>
                </select>
              </div>
            </div>
            <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
              <label className="block text-sm font-semibold text-purple-900 mb-2">
                Special categories
              </label>
              <div className="flex flex-wrap gap-4 text-sm">
                {SPECIAL_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="inline-flex items-center gap-2 bg-white border border-purple-100 px-3 py-2 rounded shadow-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={(editForm.special_categories || []).includes(opt)}
                      onChange={() =>
                        setEditForm((p) => ({
                          ...p,
                          special_categories: toggleSpecial(
                            p.special_categories || [],
                            opt
                          ),
                        }))
                      }
                    />
                    <span className="font-medium text-ink-800">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelEdit}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingEdit}
                className={`btn-primary ${
                  savingEdit ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th className="text-left">
                #
              </th>
              <th className="text-left">
                Name
              </th>
              <th className="text-left">
                Class
              </th>
              <th className="text-left">
                Caliber Group
              </th>
              <th className="text-left">
                Division
              </th>
              <th className="text-left">
                Special categories
              </th>
              <th className="text-left">
                NRA
              </th>
              <th className="text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {shooters.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-ink-500">
                  No shooters found
                </td>
              </tr>
            ) : (
              shooters.map((shooter) => (
                <tr
                  key={shooter.id}
                  className={editingId === shooter.id ? "bg-amber-50" : ""}
                >
                  <td className="whitespace-nowrap text-ink-600">
                    {shooter.competitor_number ?? "—"}
                  </td>
                  <td className="whitespace-nowrap">
                    <div className="font-medium text-ink-900">
                      {shooter.name}
                    </div>
                    {shooter.cmp_number && (
                      <div className="text-xs text-ink-400">CMP {shooter.cmp_number}</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap">
                    {shooter.rating || "—"}
                  </td>
                  <td className="whitespace-nowrap">
                    {shooter.caliber_group || "Open"}
                  </td>
                  <td className="whitespace-nowrap">
                    {shooter.division || "Civilian"}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(shooter.special_categories || []).length === 0 ? (
                        <span className="text-xs text-ink-400">—</span>
                      ) : (
                        (shooter.special_categories || []).map((c) => (
                          <span
                            key={c}
                            className="badge-purple"
                          >
                            {c}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap text-ink-500">
                    {shooter.nra_number || "—"}
                  </td>
                  <td className="whitespace-nowrap text-right font-medium space-x-3">
                    <Link
                      to={`/shooters/${shooter.id}`}
                      className="text-brand-600 hover:text-brand-900"
                    >
                      View
                    </Link>
                    {isAdmin() && (
                      <>
                        <button
                          type="button"
                          onClick={() => startEdit(shooter)}
                          className="text-brand-600 hover:text-brand-900"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(shooter)}
                          disabled={deletingId === shooter.id}
                          className="text-rose-600 hover:text-rose-900 disabled:opacity-50"
                        >
                          {deletingId === shooter.id ? "Deleting..." : "Delete"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShootersList;
